import { useQuery, useQueryClient } from '@tanstack/react-query'
import type { ColDef, ICellRendererParams } from 'ag-grid-community'
import { useMemo } from 'react'
import { Link, useNavigate } from 'react-router'

import { api, can, type SourceRow } from '../api'
import { Pill, RunStatus } from '../components'
import { DataGrid, type BulkAction, type GridFilter } from '../DataGrid'
import { dt, statusKind } from '../format'
import { pollWhile } from '../hooks'

const opts = (...v: string[]) => v.map((x) => ({ value: x, label: x }))

const licenceState = (s: SourceRow) =>
  !s.license ? 'none' : s.license_errors > 0 ? 'blocking' : s.license_warnings > 0 ? 'warnings' : 'complete'

const sourceFilters: GridFilter<SourceRow>[] = [
  { key: 'status', label: 'Status', options: opts('active', 'draft', 'paused', 'disabled'), value: (s) => s.status },
  { key: 'licence', label: 'Licence', options: opts('complete', 'warnings', 'blocking', 'none'), value: licenceState },
  {
    key: 'last',
    label: 'Last run',
    options: opts('succeeded', 'failed', 'skipped', 'cancelled', 'never'),
    value: (s) => s.last_run_status ?? 'never',
  },
  { key: 'access', label: 'Access', options: opts('http_download', 'http', 'axfr', 'upload', 'google_drive'), value: (s) => s.access_method },
]

function LicenseCell({ data }: ICellRendererParams<SourceRow>) {
  if (!data) return null
  if (!data.license) return <Pill kind="err">no licence</Pill>
  return (
    <span className="cell-license">
      <span>{data.license.spdx_id ?? data.license.name}</span>
      <span className="muted mono">v{data.license.version}</span>
      {data.license_errors > 0 ? (
        <Pill kind="err">{data.license_errors} blocking</Pill>
      ) : data.license_warnings > 0 ? (
        <Pill kind="warn">{data.license_warnings} warnings</Pill>
      ) : (
        <Pill kind="ok">complete</Pill>
      )}
    </span>
  )
}

export function Sources() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const me = useQuery({ queryKey: ['me'], queryFn: api.me })
  const editor = can(me.data, 'editor')
  const actions = useMemo<BulkAction<SourceRow>[]>(
    () =>
      editor
        ? [
            { label: 'Set active', primary: true, applies: (s) => s.status !== 'active', run: (s) => api.patchSource(s.slug, { status: 'active' }) },
            { label: 'Back to draft', applies: (s) => s.status !== 'draft', run: (s) => api.patchSource(s.slug, { status: 'draft' }) },
            { label: 'Pause', applies: (s) => s.status === 'active', run: (s) => api.patchSource(s.slug, { status: 'paused' }) },
            { label: 'Run now', applies: (s) => !s.active_run, run: (s) => api.triggerRun(s.slug) },
          ]
        : [],
    [editor],
  )
  const q = useQuery({
    queryKey: ['sources'],
    queryFn: api.sources,
    refetchInterval: (query) => pollWhile(!!query.state.data?.some((s) => s.active_run), 3_000, 30_000),
  })

  const cols = useMemo<ColDef<SourceRow>[]>(
    () => [
      {
        field: 'name',
        headerName: 'Source',
        flex: 2,
        minWidth: 220,
        cellRenderer: ({ data }: ICellRendererParams<SourceRow>) =>
          data ? (
            <span className="cell-two">
              <Link to={`/sources/${data.slug}`}>{data.name}</Link>
              <span className="muted">{data.publisher}</span>
            </span>
          ) : null,
      },
      { field: 'usage_mode', headerName: 'Usage', width: 120, cellClass: 'mono' },
      { headerName: 'Licence', flex: 2, minWidth: 260, cellRenderer: LicenseCell, sortable: false },
      { headerName: 'Checked', width: 120, valueGetter: (p) => p.data?.license?.checked_at ?? '–' },
      {
        field: 'status',
        width: 110,
        cellRenderer: ({ value }: ICellRendererParams<SourceRow, string>) => <Pill kind={statusKind(value)}>{value}</Pill>,
      },
      {
        headerName: 'Last run',
        width: 260,
        valueGetter: (p) => p.data?.last_run_at,
        cellRenderer: ({ data }: ICellRendererParams<SourceRow>) =>
          data?.active_run ? (
            <RunStatus run={data.active_run} />
          ) : data?.last_run_at ? (
            <span>
              {dt(data.last_run_at)} <Pill kind={statusKind(data.last_run_status)}>{data.last_run_status}</Pill>
            </span>
          ) : (
            <span className="muted">never</span>
          ),
      },
      { headerName: 'Next run', width: 150, valueGetter: (p) => p.data?.next_run, valueFormatter: (p) => dt(p.value) },
      { field: 'access_method', headerName: 'Access', width: 120, cellClass: 'mono' },
    ],
    [],
  )

  return (
    <section className="page">
      <header className="page-h">
        <div>
          <h1>Sources</h1>
          <p className="sub">Upstream datasets, how we may use them, and the licence each one is published under.</p>
        </div>
      </header>
      {q.error && <p className="callout err">{q.error.message}</p>}
      <DataGrid<SourceRow>
        rows={q.data}
        columnDefs={cols}
        loading={q.isLoading}
        getRowId={(s) => s.slug}
        searchText={(s) => [s.name, s.slug, s.publisher, s.license?.name, s.license?.spdx_id, s.access_method].join(' ')}
        filters={sourceFilters}
        actions={actions}
        onDone={() => qc.invalidateQueries({ queryKey: ['sources'] })}
        onOpen={(s) => navigate(`/sources/${s.slug}`)}
      />
    </section>
  )
}
